import * as moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { NumericFormat } from "react-number-format";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  Row,
  RSelect,
} from "../../../components/Component";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { GET, PATCH } from "../../../helper/api";
import { useUser } from "../../../context/user";
import { INGREDIENT_CATEGORY_NAME, USER_ROLES } from "../../../utils/Utils";

const ALLOWED_ROLES = [USER_ROLES.ENTRY_STAFF, USER_ROLES.MANAGER, USER_ROLES.SHIFT_LEADER, USER_ROLES.ADMIN];
const REJECT_FROM_LABELS = {
  INTERNAL: "Từ chối bởi người điều xe",
  EXTERNAL: "Yêu cầu huỷ từ khách",
};

const DRIVER_STATUS_LABEL = {
  PENDING: "Chờ bắt đầu",
  STARTED: "Đang chạy",
  PICKED_UP: "Đã lấy hàng",
  COMPLETED: "Đã hoàn thành",
  CANCELLED: "Đã huỷ",
};

const DRIVER_STATUS_COLOR = {
  PENDING: "secondary",
  STARTED: "warning",
  PICKED_UP: "info",
  COMPLETED: "success",
  CANCELLED: "danger",
};

const LOCKED_STATUSES = ["PICKED_UP", "COMPLETED"];
// Chuyến đã xong/huỷ thì không cần (và không cho) đổi kho điều xe về
const FINISHED_STATUSES = ["COMPLETED", "CANCELLED"];

const PNRequestBillDispatch = () => {
  const history = useHistory();
  const { id: pnRequestBillId } = useParams();

  const {
    state: { user: currentUser },
  } = useUser();

  const canAccess = ALLOWED_ROLES.includes(currentUser?.role);

  const [bill, setBill] = useState(null);
  const [driverStatuses, setDriverStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelResponding, setCancelResponding] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [driverOptions, setDriverOptions] = useState([]);
  const [truckOptions, setTruckOptions] = useState([]);
  const [pnCustomers, setPnCustomers] = useState([]);
  const [locationDistances, setLocationDistances] = useState([]);
  const [driveRentalContract, setDriveRentalContract] = useState(null);

  const {
    control,
    handleSubmit,
    register,
    reset,
    setValue,
    formState: { errors },
  } = useForm({ defaultValues: { trips: [] }, mode: "onChange" });

  const { fields, append, remove } = useFieldArray({ control, name: "trips" });

  useEffect(() => {
    if (!canAccess) {
      history.replace("/");
      return;
    }
    if (!pnRequestBillId) {
      toast.error("Thiếu mã phiếu điều xe");
      history.replace("/");
      return;
    }
    loadBill();
    loadOptions();
  }, []);

  const loadBill = () => {
    setLoading(true);
    GET(`/pn/pn-request-bill/${pnRequestBillId}`, {}, true)
      .then(({ data }) => {
        setBill(data);
        setDriverStatuses(data.driverStatuses || []);
        reset({ trips: data.trips || [] });
        const contractId = data.metadata?.driveRentalContractId;
        if (contractId) {
          GET(`/drive-rental-contract/${contractId}`, {}, true)
            .then(({ data: contract }) => setDriveRentalContract(contract))
            .catch(() => setDriveRentalContract(null));
        }
      })
      .catch(() => toast.error("Không tìm thấy phiếu điều xe"))
      .finally(() => setLoading(false));
  };

  const loadOptions = () => {
    GET("/user/list-drivers", {}, true).then((resp) => {
      const { data } = resp;
      setDriverOptions(
        data.map((c) => ({
          label: c.name,
          value: c.id,
          data: c,
        }))
      );
    });

    GET("truck", { type: "TRUCK" }, true).then(({ data }) => {
      const rows = data?.rows || [];
      setTruckOptions(rows.map((t) => ({ label: t.id, value: t.id, data: t })));
    });

    GET("/pn-customer", {}, true).then(({ data }) => {
      setPnCustomers(data?.rows || data || []);
    });

    GET("/location-distance", {}, true).then(({ data }) => {
      setLocationDistances(data || []);
    });
  };

  // Danh sách kho để chọn "Điều xe về" — lấy từ điểm A (kho) của bảng khoảng cách
  const warehouseOptions = useMemo(() => {
    const map = {};
    locationDistances.forEach((d) => {
      if (d.fromLocation) map[d.fromLocation] = d.fromLocationName;
    });
    return Object.keys(map).map((value) => ({ label: map[value] || value, value }));
  }, [locationDistances]);

  const locationDistance = useMemo(
    () => locationDistances.find((d) => d.fromLocation === bill?.fromLocation && d.toLocation === bill?.toLocation) || null,
    [locationDistances, bill?.fromLocation, bill?.toLocation],
  );

  // Mã nguyên liệu của phiếu — ưu tiên ingredientId, fallback suy ra từ tên hàng hoá
  const billIngredientId = useMemo(() => {
    if (bill?.metadata?.ingredientId) return bill.metadata.ingredientId;
    if (bill?.ingredientVATName) {
      const found = Object.keys(INGREDIENT_CATEGORY_NAME).find(
        (id) => INGREDIENT_CATEGORY_NAME[id] === bill.ingredientVATName,
      );
      if (found) return found;
    }
    return null;
  }, [bill?.ingredientId, bill?.ingredientVATName]);

  // Khi chọn xe → tự điền tài xế chính, tài xế phụ, và khối lượng theo nguyên liệu
  const handleTruckChange = (index, truckOpt) => {
    setValue(`trips.${index}.truckId`, truckOpt?.value || null, { shouldValidate: true });
    const truck = truckOpt?.data;
    if (!truck) return;

    if (truck.defaultDriverId != null) {
      setValue(`trips.${index}.driverId`, truck.defaultDriverId, { shouldValidate: true });
    }
    if (truck.defaultSubDriverId != null) {
      setValue(`trips.${index}.subDriverId`, truck.defaultSubDriverId);
    }
    const defaultWeight = billIngredientId
      ? truck.defaultWeightByIngredient?.[billIngredientId]
      : null;
    if (defaultWeight != null) {
      setValue(`trips.${index}.quantity`, defaultWeight, { shouldValidate: true });
    }
  };

  const getDriverStatus = (itemId, driverId) => {
    if (!itemId) return null;
    return driverStatuses.find((s) => s.tripItemId === itemId && s.driverId === driverId) || null;
  };

  const submitDrivers = (values, isAccept = false) => {
    setSubmitting(true);
    PATCH(
      `/pn/pn-request-bill/${pnRequestBillId}/trips`,
      { trips: values.trips, userId: currentUser.id, ...(isAccept ? { isAccept: true } : {}) },
      {},
      true,
    )
      .then(() => {
        toast.success(isAccept ? "Đã duyệt và thông báo tài xế!" : "Đã lưu và thông báo tài xế!");
        loadBill();
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Có lỗi xảy ra"))
      .finally(() => setSubmitting(false));
  };

  const onSubmit = (values) => submitDrivers(values, false);

  const isRejected = !!bill?.rejectReason;
  const isCompleted = !!bill?.metadata?.completedAt;
  const isApproved = bill?.isAccept === true;
  const pendingCancelReason = (!isRejected && isApproved) ? bill?.metadata?.cancelReason : null;

  const handleCancelResponse = (approved) => {
    setCancelResponding(true);
    PATCH(`/pn/pn-request-bill/${pnRequestBillId}/cancel-response`, { approved }, {}, true)
      .then(() => {
        toast.success(approved ? "Đã đồng ý huỷ, đã thông báo cho bên đặt lịch." : "Đã từ chối yêu cầu huỷ.");
        loadBill();
      })
      .catch((err) => toast.error(err?.response?.data?.error || "Có lỗi xảy ra"))
      .finally(() => setCancelResponding(false));
  };

  const handleReject = () => {
    if (!rejectReason.trim()) {
      toast.error("Vui lòng nhập lý do từ chối");
      return;
    }
    setRejecting(true);
    PATCH(`/pn/pn-request-bill/${pnRequestBillId}/reject`, { reason: rejectReason.trim() }, {}, true)
      .then(() => {
        toast.success("Đã từ chối và thông báo cho bên đặt lịch.");
        setRejectModal(false);
        setRejectReason("");
        loadBill();
      })
      .catch((error) => {
        toast.error(error.response?.error || "Có lỗi xảy ra1")
      })
      .finally(() => setRejecting(false));
  };

  if (!canAccess) return null;

  if (loading) {
    return (
      <Content>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <Spinner color="primary" />
        </div>
      </Content>
    );
  }

  if (!bill) {
    return (
      <Content>
        <div className="text-center text-danger py-5">Không tìm thấy phiếu điều xe.</div>
      </Content>
    );
  }

  return (
    <React.Fragment>
      <Head title="Điều xe" />
      <Content>
        <BlockHead size="sm">
          <BlockHeadContent>
            <div >
              <BlockTitle page className="mb-0">Nhập Điều Xe</BlockTitle>
            </div>
            <p className="mt-2">
              {bill.isAccept === true && !bill.metadata?.completedAt && !bill.rejectReason && (
                <span style={{ fontSize: 13 }} className="p-1 bg-success text-white">Đã duyệt</span>
              )}
              {bill.metadata?.completedAt && <span style={{ fontSize: 13 }} className="p-1 bg-primary text-white">Hoàn thành</span>}
              {bill.rejectReason && (
                <h4 className="text-danger">{REJECT_FROM_LABELS[bill.rejectFrom] || "Lý do"}: {bill.rejectReason}</h4>
              )}
            </p>

          </BlockHeadContent>
        </BlockHead>

        <Block>
          {/* Banner yêu cầu huỷ từ khách */}
          {pendingCancelReason && (
            <div className="alert alert-warning d-flex align-items-start gap-3 mb-3">
              <div className="flex-grow-1">
                <strong>Yêu cầu huỷ từ khách</strong>
                <div className="mt-1">Lý do: {pendingCancelReason}</div>
              </div>
              <div className="d-flex gap-2 flex-shrink-0">
                <Button
                  color="success"
                  size="sm"
                  disabled={cancelResponding}
                  onClick={() => handleCancelResponse(true)}
                >
                  {cancelResponding ? <Spinner size="sm" color="light" className="mr-1" /> : null}
                  Đồng ý huỷ
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  disabled={cancelResponding}
                  onClick={() => handleCancelResponse(false)}
                >
                  Từ chối huỷ
                </Button>
              </div>
            </div>
          )}

          {/* Read-only info */}
          <div className="card card-bordered mb-3">
            <div className="card-inner">
              <h6 className="card-title mb-3">Thông tin phiếu</h6>
              <Row>
                <Col md={4}>
                  <div className="form-group">
                    <label className="form-label">Hàng hoá (VAT)</label>
                    <input className="form-control fw-bold" disabled value={bill.ingredientVATName || "—"} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="form-label">Chiều H.Hoá</label>
                    <input className="form-control" disabled value={bill.customerDirection || "—"} />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="form-label">Công nợ</label>
                    <input
                      className="form-control"
                      disabled
                      value={pnCustomers.find((c) => c.id === bill.customerId)?.name || bill.customerId || "—"}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="form-label">Số lượng (kg)</label>
                    <input
                      className="form-control fw-bold text-primary"
                      disabled
                      value={bill.weight ? `${bill.weight.toLocaleString("en-US")} kg` : "—"}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="form-label">Ngày</label>
                    <input
                      className="form-control"
                      disabled
                      value={bill.startDate ? moment(bill.startDate).format("DD/MM/YYYY") : "—"}
                    />
                  </div>
                </Col>
                <Col md={4}>
                  <div className="form-group">
                    <label className="form-label">Buổi</label>
                    <input className="form-control" disabled value={bill.startShift || "—"} />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Điểm A (Từ)</label>
                    <input
                      className="form-control"
                      disabled
                      value={locationDistance?.fromLocationName || bill.fromLocation || "—"}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form-group">
                    <label className="form-label">Điểm B (Đến)</label>
                    <input
                      className="form-control"
                      disabled
                      value={locationDistance?.toLocationName || bill.toLocation || "—"}
                    />
                  </div>
                </Col>
                {bill.metadata?.driveRentalContractId && (
                  <Col md={12}>
                    <div className="form-group">
                      <label className="form-label">Hợp đồng thuê xe</label>
                      <input
                        className="form-control"
                        disabled
                        value={[
                          driveRentalContract?.customer?.name,
                          `HĐ số ${bill.metadata.driveRentalContractId}`,
                          driveRentalContract?.info && `Từ "${driveRentalContract.info.from}" đến "${driveRentalContract.info.to}"`,
                        ].filter(Boolean).join(" - ")}
                      />
                    </div>
                  </Col>
                )}
                {bill.metadata?.note && (
                  <Col md={12}>
                    <div className="form-group">
                      <label className="form-label">Ghi chú</label>
                      <input className="form-control" disabled value={bill.metadata.note} />
                    </div>
                  </Col>
                )}
                {locationDistances.length > 0 && bill.fromLocation && bill.toLocation && !locationDistance && (
                  <Col md={12}>
                    <small className="text-danger">Vui lòng liên hệ người quản lý gấp để cập nhật khoảng cách địa chỉ này.</small>
                  </Col>
                )}
              </Row>
            </div>
          </div>

          {/* Drivers form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="card card-bordered">
              <div className="card-inner">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="card-title mb-0">Danh sách chuyến xe</h6>
                  <Button
                    type="button"
                    color="primary"
                    size="sm"
                    onClick={() => append({ driverId: null, subDriverId: null, truckId: null, quantity: 0, returnWarehouseId: null, returnWarehouseName: "" })}
                  >
                    <Icon name="plus" /> Thêm chuyến xe
                  </Button>
                </div>

                <div>
                  {fields.map((field, index) => {
                    const itemId = field.itemId;
                    const ds = getDriverStatus(itemId, field.driverId);
                    const locked = ds && LOCKED_STATUSES.includes(ds.status);
                    const finished = ds && FINISHED_STATUSES.includes(ds.status);
                    const statusColor = ds ? DRIVER_STATUS_COLOR[ds.status] : null;
                    const statusLabel = ds ? DRIVER_STATUS_LABEL[ds.status] : null;

                    return (
                      <div key={field.id} className="card card-bordered mb-2">
                        <div className="card-inner p-2">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-bold text-muted" style={{ fontSize: 13 }}>Chuyến #{index + 1}</span>
                              {statusLabel && (
                                <span className={`badge bg-${statusColor}`} style={{ fontSize: 11 }}>
                                  {statusLabel}
                                </span>
                              )}
                              {locked && (
                                <span className="text-danger" style={{ fontSize: 11 }}>
                                  <Icon name="lock" /> Không thể sửa/xoá
                                </span>
                              )}
                            </div>
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              onClick={() => remove(index)}
                              disabled={locked}
                              title={locked ? "Tài xế đã lấy hàng/hoàn thành, không thể xoá" : ""}
                            >
                              <Icon name="trash" />
                            </Button>
                          </div>
                          {/* Hidden fields để RHF đưa vào values khi submit */}
                          <Controller control={control} name={`trips.${index}.itemId`} defaultValue={field.itemId || ''} render={() => null} />
                          <Controller control={control} name={`trips.${index}.telegramMessageId`} defaultValue={field.telegramMessageId ?? null} render={() => null} />
                          <Controller control={control} name={`trips.${index}.telegramChatId`} defaultValue={field.telegramChatId || ''} render={() => null} />
                          <Controller control={control} name={`trips.${index}.returnWarehouseName`} defaultValue={field.returnWarehouseName || ''} render={() => null} />

                          <Row className="g-2">
                            <Col xs={12} md={4}>
                              <div className="form-group mb-0">
                                <label className="form-label form-label-sm">Tài xế chính <span className="text-danger">*</span></label>
                                <Controller
                                  control={control}
                                  name={`trips.${index}.driverId`}
                                  defaultValue={field.driverId ?? null}
                                  rules={{ required: "Bắt buộc" }}
                                  render={({ onChange, value }) => (
                                    <RSelect
                                      options={driverOptions}
                                      placeholder="Chọn tài xế..."
                                      value={driverOptions.find((o) => o.value === value) || null}
                                      onChange={(opt) => onChange(opt?.value || null)}
                                      isDisabled={locked}
                                    />
                                  )}
                                />
                                {errors?.trips?.[index]?.driverId && (
                                  <small className="text-danger">{errors.trips[index].driverId.message}</small>
                                )}
                              </div>
                            </Col>
                            <Col xs={12} md={4}>
                              <div className="form-group mb-0">
                                <label className="form-label form-label-sm">Tài xế phụ</label>
                                <Controller
                                  control={control}
                                  name={`trips.${index}.subDriverId`}
                                  defaultValue={field.subDriverId ?? null}
                                  render={({ onChange, value }) => (
                                    <RSelect
                                      options={driverOptions}
                                      placeholder="Không có..."
                                      isClearable
                                      value={driverOptions.find((o) => o.value === value) || null}
                                      onChange={(opt) => onChange(opt?.value || null)}
                                      isDisabled={locked}
                                    />
                                  )}
                                />
                              </div>
                            </Col>
                            <Col xs={6} md={2}>
                              <div className="form-group mb-0">
                                <label className="form-label form-label-sm">Xe <span className="text-danger">*</span></label>
                                <Controller
                                  control={control}
                                  name={`trips.${index}.truckId`}
                                  defaultValue={field.truckId ?? null}
                                  rules={{ required: "Bắt buộc" }}
                                  render={({ value }) => (
                                    <RSelect
                                      options={truckOptions}
                                      placeholder="Chọn xe..."
                                      value={truckOptions.find((o) => o.value === value) || null}
                                      onChange={(opt) => handleTruckChange(index, opt)}
                                      isDisabled={locked}
                                    />
                                  )}
                                />
                                {errors?.trips?.[index]?.truckId && (
                                  <small className="text-danger">{errors.trips[index].truckId.message}</small>
                                )}
                              </div>
                            </Col>
                            <Col xs={6} md={2}>
                              <div className="form-group mb-0">
                                <label className="form-label form-label-sm">Số lượng (kg) <span className="text-danger">*</span></label>
                                <Controller
                                  control={control}
                                  name={`trips.${index}.quantity`}
                                  defaultValue={field.quantity ?? 0}
                                  rules={{ required: "Bắt buộc", min: { value: 1, message: "Tối thiểu 1" } }}
                                  render={({ onChange, value }) => (
                                    <NumericFormat
                                      className="form-control"
                                      value={value}
                                      thousandSeparator=","
                                      onValueChange={({ floatValue }) => onChange(floatValue || 0)}
                                      placeholder="0"
                                      disabled={locked}
                                    />
                                  )}
                                />
                                {errors?.trips?.[index]?.quantity && (
                                  <small className="text-danger">{errors.trips[index].quantity.message}</small>
                                )}
                              </div>
                            </Col>
                            <Col xs={12} md={4}>
                              <div className="form-group mb-0">
                                <label className="form-label form-label-sm">
                                  Điều xe về {!finished && <span className="text-danger">*</span>}
                                </label>
                                <Controller
                                  control={control}
                                  name={`trips.${index}.returnWarehouseId`}
                                  defaultValue={field.returnWarehouseId ?? null}
                                  rules={finished ? {} : { required: "Bắt buộc" }}
                                  render={({ onChange, value }) => (
                                    <RSelect
                                      options={warehouseOptions}
                                      placeholder="Chọn kho..."
                                      value={warehouseOptions.find((o) => o.value === value) || null}
                                      onChange={(opt) => {
                                        onChange(opt?.value || null);
                                        setValue(`trips.${index}.returnWarehouseName`, opt?.label || "");
                                      }}
                                      isDisabled={finished}
                                    />
                                  )}
                                />
                                {errors?.trips?.[index]?.returnWarehouseId && (
                                  <small className="text-danger">{errors.trips[index].returnWarehouseId.message}</small>
                                )}
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-3 mb-3 d-flex flex-wrap gap-1 justify-content-end">
                  <Button color="primary" type="submit" disabled={submitting || isRejected || isCompleted}>
                    {submitting ? <Spinner size="sm" color="light" className="mr-1" /> : null}
                    Lưu
                  </Button>
                  {!isApproved && !isRejected && !isCompleted && (
                    <Button
                      color="success"
                      type="button"
                      disabled={submitting || !fields?.length}
                      onClick={handleSubmit((values) => submitDrivers(values, true))}
                    >
                      Duyệt
                    </Button>
                  )}
                  <Button
                    type="button"
                    color="danger"
                    disabled={submitting || isRejected || isCompleted}
                    onClick={() => setRejectModal(true)}
                  >
                    Từ chối
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </Block>
      </Content>
      <Modal isOpen={rejectModal} toggle={() => setRejectModal(false)} className="modal-dialog-centered">
        <ModalHeader toggle={() => setRejectModal(false)}>Từ chối phiếu điều xe</ModalHeader>
        <ModalBody>
          <div className="form-group">
            <label className="form-label">Lý do từ chối <span className="text-danger">*</span></label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Nhập lý do từ chối..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={handleReject} disabled={rejecting}>
            {rejecting ? <Spinner size="sm" color="light" className="mr-1" /> : null}
            Xác nhận từ chối
          </Button>
          <Button color="light" onClick={() => { setRejectModal(false); setRejectReason(""); }}>
            Huỷ
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default PNRequestBillDispatch;
